import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Stack,
  Avatar,
} from '@mui/material'
import { Delete, Add, DragIndicator } from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import SegmentFields from './SegmentFields'
import { getIcon } from '../NavigationItem'

export interface SegmentInspectorProps {
  segment: any
  segmentIndex: number
  loopIndex: number
  onChange: (data: any) => void
  onNavigateToElement?: (element: any, elementIndex: number) => void
  readonly?: boolean
}

interface SortableElementItemProps {
  element: any
  sortId: string
  displayIndex: number
  originalIndex: number
  readonly: boolean
  canNavigate: boolean
  onNavigate: () => void
  onRemove: () => void
}

const SortableElementItem: React.FC<SortableElementItemProps> = ({
  element,
  sortId,
  displayIndex,
  readonly,
  canNavigate,
  onNavigate,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
    disabled: readonly,
  })

  return (
    <ListItem
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        cursor: canNavigate ? 'pointer' : 'default',
        '&:hover': canNavigate ? { backgroundColor: 'action.hover' } : {},
      }}
      onClick={canNavigate ? onNavigate : undefined}
    >
      {!readonly && (
        <IconButton
          size="small"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab', mr: 1, touchAction: 'none' }}
          onClick={e => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
      )}
      <ListItemText
        primary={
          <Box>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
              {element.name || `Element ${displayIndex + 1}`}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                }}
              >
                {element.position || displayIndex + 1}
              </Avatar>
              <Chip
                label={`Usage: ${element.usage || 'M'}`}
                size="small"
                color={element.usage === 'M' ? 'error' : 'default'}
                variant="outlined"
              />
              <Chip
                label={`Type: ${element.data_type || 'string'}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`Length: ${element.min_length || 0}-${element.max_length || 0}`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Stack>
          </Box>
        }
      />
      <ListItemSecondaryAction>
        {!readonly && (
          <IconButton
            edge="end"
            onClick={e => {
              e.stopPropagation()
              onRemove()
            }}
            size="small"
          >
            <Delete />
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const getSortId = (element: any, index: number): string => element.id || String(index)

const SegmentInspector = React.memo<SegmentInspectorProps>(
  ({ segment, segmentIndex, loopIndex, onChange, onNavigateToElement, readonly = false }) => {
    const [localSegment, setLocalSegment] = useState(segment)

    useEffect(() => {
      setLocalSegment(segment)
    }, [segment])

    const sortedElements = useMemo(() => {
      if (!localSegment.elements) return []
      return [...localSegment.elements].sort((segmentElementA, segmentElementB) => {
        const posA = segmentElementA.position || 0
        const posB = segmentElementB.position || 0
        return posA - posB
      })
    }, [localSegment.elements])

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const handleFieldChange = useCallback(
      (field: string, value: any) => {
        const updatedSegment = {
          ...localSegment,
          [field]: value,
        }
        setLocalSegment(updatedSegment)
        onChange(updatedSegment)
      },
      [localSegment, onChange]
    )

    const handleAddElement = useCallback(() => {
      const newElement = {
        id: `element-${Date.now()}`,
        name: 'New Element',
        position: 1,
        usage: 'M',
        data_type: 'string',
        min_length: 1,
        max_length: 10,
      }

      const existingSorted = [...(localSegment.elements || [])].sort(
        (segmentElementA: any, segmentElementB: any) =>
          (segmentElementA.position || 0) - (segmentElementB.position || 0)
      )
      const updatedElements = [newElement, ...existingSorted].map((el: any, idx: number) => ({
        ...el,
        position: idx + 1,
      }))
      const updatedSegment = {
        ...localSegment,
        elements: updatedElements,
      }
      setLocalSegment(updatedSegment)
      onChange(updatedSegment)
    }, [localSegment, onChange])

    const handleRemoveElement = useCallback(
      (element: any) => {
        const updatedElements = (localSegment.elements?.filter((e: any) => e !== element) || [])
          .sort(
            (segmentElementA: any, segmentElementB: any) =>
              (segmentElementA.position || 0) - (segmentElementB.position || 0)
          )
          .map((el: any, idx: number) => ({ ...el, position: idx + 1 }))
        const updatedSegment = {
          ...localSegment,
          elements: updatedElements,
        }
        setLocalSegment(updatedSegment)
        onChange(updatedSegment)
      },
      [localSegment, onChange]
    )

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const oldIndex = sortedElements.findIndex(
          (el: any, i: number) => getSortId(el, i) === active.id
        )
        const newIndex = sortedElements.findIndex(
          (el: any, i: number) => getSortId(el, i) === over.id
        )

        const reordered = arrayMove([...sortedElements], oldIndex, newIndex)
        const withUpdatedPositions = reordered.map((el: any, idx: number) => ({
          ...el,
          position: idx + 1,
        }))

        const updatedSegment = {
          ...localSegment,
          elements: withUpdatedPositions,
        }
        setLocalSegment(updatedSegment)
        onChange(updatedSegment)
      },
      [sortedElements, localSegment, onChange]
    )

    if (!localSegment) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No segment data available
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2 }}>
        {/* Segment Properties */}
        <Paper elevation={1} sx={{ p: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={'bold'} pb={1}>
            Segment Properties
          </Typography>
          <SegmentFields
            segment={localSegment}
            readonly={readonly}
            onFieldChange={handleFieldChange}
            showId={true}
            showName={true}
            showUsage={true}
            showMaxUse={true}
          />
        </Paper>

        {/* Elements Section */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getIcon('element')}
              <Typography variant="h6" fontWeight={'bold'}>
                Elements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({sortedElements.length || 0})
              </Typography>
            </Box>
            {!readonly && (
              <Button
                startIcon={<Add />}
                onClick={handleAddElement}
                size="small"
                variant="outlined"
              >
                Add Element
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {sortedElements && sortedElements.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedElements.map((el: any, i: number) => getSortId(el, i))}
                strategy={verticalListSortingStrategy}
              >
                <List dense>
                  {sortedElements.map((element: any, index: number) => {
                    const originalIndex = localSegment.elements.indexOf(element)
                    return (
                      <SortableElementItem
                        key={getSortId(element, index)}
                        element={element}
                        sortId={getSortId(element, index)}
                        displayIndex={index}
                        originalIndex={originalIndex}
                        readonly={readonly}
                        canNavigate={!!onNavigateToElement}
                        onNavigate={() => onNavigateToElement?.(element, originalIndex)}
                        onRemove={() => handleRemoveElement(element)}
                      />
                    )
                  })}
                </List>
              </SortableContext>
            </DndContext>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No elements defined
            </Typography>
          )}
        </Paper>
      </Box>
    )
  }
)

SegmentInspector.displayName = 'SegmentInspector'

export default SegmentInspector
